import { useCallback, useEffect, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconAlertTriangle, IconEdit } from '../components/icons';
import { Modal } from '../components/Modal';
import { AllocationTools } from '../components/investments/AllocationTools';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatMoney } from '../utils/format';

interface AllocationRow {
  asset_class: string;
  label: string;
  amount: string;
  percent: number;
  policy_min: string | null;
  policy_max: string | null;
  breach: boolean | null;
  warr: number;
}

interface Allocation {
  total: string;
  warr: number;
  by_class: AllocationRow[];
}

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

interface PolicyRow {
  id: number;
  asset_class: string;
  min_percent: string;
  max_percent: string;
}

// The asset-allocation breakdown and target-range policy — the other half
// of what used to be one "Investment Management" page is now
// InvestmentAssets.tsx (the individual investment register).
export function InvestmentPolicy() {
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const canConfigure = can('investments.configure');

  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [allocation, setAllocation] = useState<Allocation | null>(null);
  const loadAllocation = useCallback(() => {
    api
      .get<Allocation>('/investment-allocation')
      .then(({ data }) => setAllocation(data))
      .catch(() => setAllocation(null));
  }, []);
  useEffect(() => {
    loadAllocation();
  }, [loadAllocation]);

  const [policyModal, setPolicyModal] = useState<{ assetClass: string; label: string; policy: PolicyRow | null } | null>(
    null
  );
  const [policyForm, setPolicyForm] = useState({ min_percent: '', max_percent: '' });

  const openPolicy = (row: AllocationRow) => {
    const policy = row.policy_min !== null && row.policy_max !== null
      ? { id: 0, asset_class: row.asset_class, min_percent: row.policy_min, max_percent: row.policy_max }
      : null;
    setPolicyModal({ assetClass: row.asset_class, label: row.label, policy });
    setPolicyForm({ min_percent: row.policy_min ?? '', max_percent: row.policy_max ?? '' });
    setErrors({});
  };

  const submitPolicy = async (e: FormEvent) => {
    e.preventDefault();
    if (!policyModal) return;
    try {
      if (policyModal.policy) {
        // Look up the real policy id via the current policy list, since
        // the allocation breakdown doesn't carry it — asset_class is
        // unique per policy so this is an unambiguous lookup.
        const { data: existing } = await api.get<PolicyRow[]>('/investment-policies');
        const match = existing.find((p) => p.asset_class === policyModal.assetClass);
        if (match) await api.put(`/investment-policies/${match.id}`, policyForm);
      } else {
        await api.post('/investment-policies', { asset_class: policyModal.assetClass, ...policyForm });
      }
      setPolicyModal(null);
      loadAllocation();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removePolicy = async () => {
    if (!policyModal?.policy) return;
    if (!(await confirmAction(`Remove the policy range for ${policyModal.label}?`))) return;
    try {
      const { data: existing } = await api.get<PolicyRow[]>('/investment-policies');
      const match = existing.find((p) => p.asset_class === policyModal.assetClass);
      if (match) await api.delete(`/investment-policies/${match.id}`);
      setPolicyModal(null);
      loadAllocation();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Policy</h1>
      </header>

      {allocation && (
        <div className="stat-grid">
          <div className="card stat">
            <span className="stat-label">WARR (Fund-Wide)</span>
            <span className="stat-value">{formatPercent(allocation.warr)}</span>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Asset Class</th>
                <th className="right">Amount</th>
                <th className="right">% of Portfolio</th>
                <th className="right">WARR</th>
                <th>Target Range</th>
                <th>Status</th>
                {canConfigure && <th></th>}
              </tr>
            </thead>
            <tbody>
              {allocation ? (
                allocation.by_class.map((row) => (
                  <tr key={row.asset_class}>
                    <td>{row.label}</td>
                    <td className="right">{fmtMoney(Number(row.amount))}</td>
                    <td className="right">{row.percent}%</td>
                    <td className="right">{formatPercent(row.warr)}</td>
                    <td>{row.policy_min !== null ? `${row.policy_min}% – ${row.policy_max}%` : '—'}</td>
                    <td>
                      {row.breach === true && (
                        <span className="badge badge-urgent">
                          <IconAlertTriangle style={{ width: 14, height: 14, marginRight: 4 }} />
                          Breach
                        </span>
                      )}
                      {row.breach === false && <span className="badge badge-active">OK</span>}
                      {row.breach === null && <span className="muted">—</span>}
                    </td>
                    {canConfigure && (
                      <td className="right">
                        <IconButton icon={IconEdit} label="Set policy range" onClick={() => openPolicy(row)} />
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canConfigure ? 7 : 6} className="muted center">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
            {allocation && (
              <tfoot>
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="right">
                    <strong>{fmtMoney(Number(allocation.total))}</strong>
                  </td>
                  <td colSpan={canConfigure ? 5 : 4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {allocation && (
        <AllocationTools assetClasses={allocation.by_class.map((row) => ({ value: row.asset_class, label: row.label }))} />
      )}

      {policyModal && (
        <Modal title={`Policy range — ${policyModal.label}`} onClose={() => setPolicyModal(null)}>
          <form onSubmit={submitPolicy} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Min %
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={policyForm.min_percent}
                onChange={(e) => setPolicyForm({ ...policyForm, min_percent: e.target.value })}
                required
              />
              {errors.min_percent && <small className="error">{errors.min_percent[0]}</small>}
            </label>
            <label>
              Max %
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={policyForm.max_percent}
                onChange={(e) => setPolicyForm({ ...policyForm, max_percent: e.target.value })}
                required
              />
              {errors.max_percent && <small className="error">{errors.max_percent[0]}</small>}
            </label>
            <div className="form-actions span-2">
              {policyModal.policy && (
                <button type="button" className="btn btn-danger-ghost" onClick={removePolicy}>
                  Remove range
                </button>
              )}
              <button type="button" className="btn btn-ghost" onClick={() => setPolicyModal(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
